/**
 * Shared domain types for Bandmate. **Frozen contract** — every module builds
 * against this file; do not edit it from a module task (report needed changes).
 *
 * Time is measured in **steps**: 16 steps per bar, 4 bars per loop, 64 steps
 * total. Step 0 is beat 1 of bar 1. Pitches are MIDI note numbers (60 = C4).
 * A `Session` must round-trip through `JSON.stringify` unchanged.
 */

/** The four fixed tracks of a Bandmate session, in display order. */
export type TrackId = 'drums' | 'bass' | 'chords' | 'lead';
export const TRACK_IDS: readonly TrackId[] = ['drums', 'bass', 'chords', 'lead'];

/** Melodic tracks hold `Note`s; the drum track holds `DrumHit`s. */
export type MelodicTrackId = Exclude<TrackId, 'drums'>;
export const MELODIC_TRACK_IDS: readonly MelodicTrackId[] = ['bass', 'chords', 'lead'];

/** Who wrote a note or hit. Drives the amber (human) / teal (agent) grid colours. */
export type Author = 'human' | 'agent';

/** Drum lanes, in display order (top to bottom). */
export type DrumLane = 'kick' | 'snare' | 'clap' | 'hat' | 'openhat' | 'perc';
export const DRUM_LANES: readonly DrumLane[] = ['kick', 'snare', 'clap', 'hat', 'openhat', 'perc'];

/** General MIDI percussion note for each lane (channel 10). */
export const DRUM_GM_NOTE: Readonly<Record<DrumLane, number>> = {
  kick: 36,
  snare: 38,
  clap: 39,
  hat: 42,
  openhat: 46,
  perc: 37,
};

export type Mode = 'major' | 'minor';
export const MODES: readonly Mode[] = ['major', 'minor'];

/** Tonic spelled with sharps; flats are accepted at the tool boundary and normalised. */
export type Tonic = 'C' | 'C#' | 'D' | 'D#' | 'E' | 'F' | 'F#' | 'G' | 'G#' | 'A' | 'A#' | 'B';
export const TONICS: readonly Tonic[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** Key spellings accepted by tools (sharps and flats). */
export const KEY_SPELLINGS: readonly string[] = [
  'C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B',
];

/** Normalise a key spelling (`Bb`, `bb`, `A#`) to a sharp `Tonic`; null if unknown. */
export function normalizeTonic(spelling: string): Tonic | null {
  const s = spelling.trim();
  if (!s) return null;
  const letter = s[0]!.toUpperCase();
  const acc = s.slice(1).replace('♭', 'b').replace('♯', '#');
  const base: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  if (!(letter in base)) return null;
  let pc = base[letter]!;
  if (acc === '#') pc += 1;
  else if (acc === 'b') pc -= 1;
  else if (acc !== '') return null;
  return TONICS[((pc % 12) + 12) % 12]!;
}

export interface Key {
  tonic: Tonic;
  mode: Mode;
}

/** Sound preset. One shared instrument rig; a style switches drum tables, defaults, filter/reverb amounts. */
export type StyleId = 'lofi' | 'house' | 'synthwave';
export const STYLES: readonly StyleId[] = ['lofi', 'house', 'synthwave'];

/** How the chords track articulates a progression. */
export type ChordRhythm = 'pad' | 'stab' | 'arp' | 'pulse' | 'offbeat';
export const CHORD_RHYTHMS: readonly ChordRhythm[] = ['pad', 'stab', 'arp', 'pulse', 'offbeat'];

/** How the bass track is generated from the progression. */
export type BassPattern = 'root' | 'root_fifth' | 'octave' | 'walking' | 'follow_kick';
export const BASS_PATTERNS: readonly BassPattern[] = ['root', 'root_fifth', 'octave', 'walking', 'follow_kick'];

/** Drum busyness. */
export type Density = 'sparse' | 'medium' | 'busy';
export const DENSITIES: readonly Density[] = ['sparse', 'medium', 'busy'];

export const STEPS_PER_BAR = 16;
export const BARS = 4;
export const TOTAL_STEPS = STEPS_PER_BAR * BARS; // 64

export const TEMPO_MIN = 60;
export const TEMPO_MAX = 180;
export const SWING_MIN = 0;
export const SWING_MAX = 60;
export const VOLUME_MIN_DB = -24;
export const VOLUME_MAX_DB = 6;

/**
 * Pitch windows shown in the sequencer per melodic track (inclusive MIDI bounds, 3 octaves).
 * Tools clamp written notes into these windows and report `clamped`.
 */
export const TRACK_RANGE: Readonly<Record<MelodicTrackId, readonly [low: number, high: number]>> = {
  bass: [28, 63], // E1 – D#4
  chords: [48, 83], // C3 – B5
  lead: [60, 95], // C4 – B6
};

/** Bass roots are wrapped into this range (D2–D3) so they stay audible on laptop speakers. */
export const BASS_ROOT_RANGE: readonly [low: number, high: number] = [38, 50];

/** Home octave used when a tool writes a bare scale degree ("5") to a track. */
export const TRACK_HOME_OCTAVE: Readonly<Record<MelodicTrackId, number>> = { bass: 2, chords: 4, lead: 4 };

/** Voicing search window for chord voicings (MIDI, inclusive) and the preferred centre. */
export const VOICING_RANGE: readonly [low: number, high: number] = [52, 76];
export const VOICING_CENTER = 60;

/**
 * Velocity classes used in 16-character pattern strings.
 * `X` accent 1.0 · `x` normal 0.8 · `o` ghost 0.5 · `.` `-` `0` off · `1` = normal.
 */
export const VELOCITY_CLASS: Readonly<Record<'X' | 'x' | 'o', number>> = { X: 1.0, x: 0.8, o: 0.5 };
export const ACCENT_THRESHOLD = 0.9;

/** Per-track default gain in dB; the mix test asserts every value is <= -4. */
export const DEFAULT_TRACK_VOLUME_DB: Readonly<Record<TrackId, number>> = {
  drums: -4,
  bass: -8,
  chords: -12,
  lead: -10,
};

/** A note on a melodic track. `step` is absolute (0..63); `length` in steps (>= 1). */
export interface Note {
  step: number;
  pitch: number;
  length: number;
  /** 0..1; the sequencer treats >= ACCENT_THRESHOLD as an accent. */
  velocity: number;
  by: Author;
}

/** A hit on the drum track. `step` is absolute (0..63). */
export interface DrumHit {
  step: number;
  lane: DrumLane;
  velocity: number;
  by: Author;
}

export interface TrackFlags {
  muted: boolean;
  solo: boolean;
  /** Track gain in dB, VOLUME_MIN_DB..VOLUME_MAX_DB. */
  volume: number;
}

export interface MelodicTrack extends TrackFlags {
  id: MelodicTrackId;
  notes: Note[];
}

export interface DrumTrack extends TrackFlags {
  id: 'drums';
  hits: DrumHit[];
}

/** One bar of a chord progression, as written by `add_chords` or the chord strip. */
export interface ChordSlot {
  /** 0..3 */
  bar: number;
  /** Roman numeral as normalised by the parser, e.g. "VI" or "V7". */
  numeral: string;
  /** Human-readable chord name, e.g. "F" or "E7". */
  name: string;
  /** Voiced MIDI pitches, low to high. */
  pitches: number[];
  /** True when the chord is outside the key's diatonic set (e.g. bVII in major). */
  borrowed: boolean;
  /** True for harmonic-minor chords in a minor key (V, V7, vii°, vii°7). */
  altered: boolean;
}

/** Everything a Bandmate session is. Serialisable; autosaved to localStorage. */
export interface Session {
  version: 1;
  key: Key;
  tempo: number;
  swing: number;
  style: StyleId;
  metronome: boolean;
  tracks: {
    drums: DrumTrack;
    bass: MelodicTrack;
    chords: MelodicTrack;
    lead: MelodicTrack;
  };
  /** Metadata behind the chords track; null when empty or edited by hand. */
  progression: ChordSlot[] | null;
  progressionRhythm: ChordRhythm | null;
  /** Metadata behind the bass track; null when empty or edited by hand. */
  bassPattern: BassPattern | null;
  ui: {
    selectedTrack: TrackId;
    /** 0..3 */
    selectedBar: number;
  };
}

export type HumanActionKind =
  | 'toggle_step'
  | 'paint_steps'
  | 'record'
  | 'mute'
  | 'unmute'
  | 'solo'
  | 'unsolo'
  | 'volume'
  | 'clear'
  | 'set_tempo'
  | 'set_swing'
  | 'set_key'
  | 'set_style'
  | 'set_chord'
  | 'select'
  | 'undo'
  | 'play'
  | 'stop'
  | 'audition_choice'
  | 'confirm'
  | 'decline'
  | 'new_session';

/** One thing the human did, kept (last 8) for `get_session.human.recent`. */
export interface HumanAction {
  /** ms since epoch */
  at: number;
  kind: HumanActionKind;
  /** <= 80 chars, e.g. "muted bass" or "recorded 6 notes on lead". */
  detail: string;
}

/** A pending or resolved audition, exposed through `get_session.audition`. */
export interface PendingAudition {
  id: string;
  question: string;
  labels: string[];
  /** Label of the chosen option, or null while awaiting / after "neither". */
  chosen: string | null;
  /** True while the card is still open and no choice has been made. */
  awaiting: boolean;
  startedAt: number;
}

/** App mode; drives which tool groups are registered. */
export type AppMode = 'idle' | 'recording' | 'auditioning';

/** A note event captured live from the keyboard, in fractional steps against the transport. */
export interface RecordedEvent {
  pitch: number;
  /** Fractional absolute step at note-on (0 <= onStep < 64). */
  onStep: number;
  /** Fractional absolute step at note-off; may exceed 64 (wraps). */
  offStep: number;
  velocity: number;
}

/** Clamp helper used across modules. */
export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Bar (0..3) and step-within-bar (0..15) of an absolute step. */
export function splitStep(step: number): { bar: number; stepInBar: number } {
  const s = ((step % TOTAL_STEPS) + TOTAL_STEPS) % TOTAL_STEPS;
  return { bar: Math.floor(s / STEPS_PER_BAR), stepInBar: s % STEPS_PER_BAR };
}

/** Absolute step from bar (0..3) and step-within-bar (0..15). */
export function joinStep(bar: number, stepInBar: number): number {
  return bar * STEPS_PER_BAR + stepInBar;
}

/** Deep clone via JSON (sessions are plain data). */
export function cloneSession(s: Session): Session {
  return JSON.parse(JSON.stringify(s)) as Session;
}
